import React, { useState } from "react";
import { FaBullhorn, FaPaperclip, FaChevronDown, FaChevronUp } from "react-icons/fa";
import Loader from "../../../components/Loader/Loader";
import { useStudentCirculars } from "./hooks/useStudentCirculars";
import { useStudentSession } from "../context/StudentSessionContext";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";
import "./StudentCircular.css";

// Circular items may expose the attachment under any of these reasonable
// field-name variants depending on the backend serializer.
const pick = (item, keys, fallback = "") => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const StudentCircular = () => {
  const { student } = useStudentSession();
  const { circulars, isLoading } = useStudentCirculars(
    student?.id,
    student?.student_program?.program_id,
    student?.student_program?.session_id
  );
  // Circular descriptions are long, school-authored rich text (see
  // circular_desc in the real response) — collapsed by default, one row at
  // a time, so the list stays scannable instead of a wall of text.
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (key) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="student-circular">
      <h5 className="sp-section-title">Circulars</h5>

      {isLoading ? (
        <Loader />
      ) : circulars.length === 0 ? (
        <div className="sp-card student-circular-empty text-center">
          <span className="sp-icon-tile">
            <FaBullhorn size={18} />
          </span>
          <p className="mb-0 text-muted">No circulars have been published yet.</p>
        </div>
      ) : (
        <div className="student-circular-list">
          {circulars.map((circular, index) => {
            // Confirmed against the real backend (2026-10-22): circular_topic /
            // circular_desc (HTML, school-authored) / circular_no / circular_file.
            const title = pick(circular, ["circular_topic", "title", "subject", "name"], "Untitled Circular");
            const description = pick(circular, ["circular_desc", "description", "details", "message", "content"]);
            const date = pick(circular, ["date", "circular_date", "created_at", "published_at"]);
            const attachment = pick(circular, [
              "circular_file",
              "attachment",
              "file",
              "file_url",
              "document",
              "attachment_url",
              "document_url",
            ]);
            const key = circular?.id ?? circular?.circular_id ?? index;
            const isExpanded = expandedIds.has(key);

            return (
              <div key={key} className="sp-card student-circular-card">
                <div className="student-circular-head">
                  <h6 className="student-circular-title">{title}</h6>
                  {date && <p className="student-circular-date text-muted mb-1">{formatDate(date)}</p>}
                </div>

                {description && (
                  <>
                    <div
                      className={`student-circular-desc text-muted${isExpanded ? " is-expanded" : ""}`}
                      // eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in sanitizeHtml() above
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
                    />
                    <button
                      type="button"
                      className="student-circular-toggle"
                      onClick={() => toggleExpanded(key)}
                    >
                      {isExpanded ? (
                        <>
                          Show less <FaChevronUp className="ms-1" />
                        </>
                      ) : (
                        <>
                          Read more <FaChevronDown className="ms-1" />
                        </>
                      )}
                    </button>
                  </>
                )}

                {attachment && (
                  <a
                    href={attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="student-circular-link"
                  >
                    <FaPaperclip className="me-1" />
                    View Attachment
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentCircular;
